import { z } from 'zod';

export const submitterRoleSchema = z.enum(['coach', 'student', 'parent', 'mentor', 'other']);
export type SubmitterRole = z.infer<typeof submitterRoleSchema>;

export const storyStatusSchema = z.enum([
  'draft',
  'pending',
  'approved',
  'rejected',
  'needs_revision',
]);
export type StoryStatus = z.infer<typeof storyStatusSchema>;

export const mediaTypeSchema = z.enum(['team_photo', 'robot_photo', 'logo', 'other']);
export type MediaType = z.infer<typeof mediaTypeSchema>;

export const mediaStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type MediaStatus = z.infer<typeof mediaStatusSchema>;

export const teamStorySubmissionSchema = z.object({
  teamNumber: z
    .string()
    .min(1, 'Team number is required')
    .max(16, 'Team number too long')
    .regex(/^[A-Za-z0-9-]+$/, 'Letters, numbers, and dashes only'),
  submittedByName: z.string().min(2, 'Name is required').max(120),
  submittedByEmail: z.email('Valid email required'),
  submitterRole: submitterRoleSchema,
  body: z.string().min(40, 'Bio must be at least 40 characters').max(4000),
  robotDescription: z.string().max(2000).optional().nullable(),
  seasonHighlight: z.string().max(2000).optional().nullable(),
  socialLinks: z.record(z.string(), z.url()).optional().nullable(),
  mediaKeys: z.array(z.string().min(1)).max(8).optional().default([]),
  permissionConfirmed: z.literal(true, {
    error: 'You must confirm permission to submit.',
  }),
});
export type TeamStorySubmissionInput = z.infer<typeof teamStorySubmissionSchema>;

export const teamStorySchema = z.object({
  id: z.uuid(),
  teamId: z.uuid(),
  teamNumber: z.string(),
  body: z.string(),
  robotDescription: z.string().nullable(),
  seasonHighlight: z.string().nullable(),
  submittedByName: z.string(),
  submittedByEmail: z.email(),
  submitterRole: submitterRoleSchema,
  status: storyStatusSchema,
  reviewedBy: z.string().nullable(),
  reviewedAt: z.iso.datetime().nullable(),
  reviewerNote: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TeamStory = z.infer<typeof teamStorySchema>;

export const mediaAssetSchema = z.object({
  id: z.uuid(),
  teamId: z.uuid().nullable(),
  storyId: z.uuid().nullable(),
  url: z.url(),
  storageKey: z.string(),
  type: mediaTypeSchema,
  status: mediaStatusSchema,
  uploadedBy: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type MediaAsset = z.infer<typeof mediaAssetSchema>;

export const presignUploadRequestSchema = z.object({
  filename: z.string().min(1).max(200),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  type: mediaTypeSchema,
  byteSize: z.number().int().positive().max(5 * 1024 * 1024, 'Max 5 MB'),
});
export type PresignUploadRequest = z.infer<typeof presignUploadRequestSchema>;

export const presignUploadResponseSchema = z.object({
  uploadUrl: z.url(),
  storageKey: z.string(),
  publicUrl: z.url(),
  method: z.enum(['PUT']),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.iso.datetime(),
});
export type PresignUploadResponse = z.infer<typeof presignUploadResponseSchema>;
